import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Ban, MailX, Megaphone, RotateCw, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi, useClientOptions } from "@/hooks/useApi";
import { FilterBar, DataTable, StatusBadge, ErrorBox, Loading } from "@/components/common/ui-bits";
import { DateInput } from "@/components/common/DateInput";
import { EmailFrame } from "@/components/notifications/TemplateEditor";
import { fmtDateTime, fmtDate, money, optionList, EMAIL_STATUSES } from "@/lib/format";

const NEEDS = { factura_emitida: "invoice", pago_recibido: "invoice", renovacion_recordatorio: "renewal", renovacion_aprobacion: "renewal", renovacion_confirmada: "renewal", proyecto_entregado: "project" };
const REL_KEY = { invoice: "invoice_id", renewal: "renewal_id", project: "project_id" };
const REL_LABEL = { invoice: "Factura", renewal: "Renovación", project: "Proyecto" };

function LogViewer({ id, onClose }) {
  const { data } = useApi(id ? `/email-log/${id}` : null);
  return (
    <Dialog open={Boolean(id)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="email-log-viewer">
        <DialogHeader><DialogTitle className="font-display">{data?.subject || "Correo"}</DialogTitle>
          <DialogDescription>{data ? `Para ${data.to} · ${data.status}${data.scheduled_at ? ` · programado ${fmtDateTime(data.scheduled_at)}` : ""}${data.error ? ` · ${data.error}` : ""} · intentos: ${data.attempts}` : ""}</DialogDescription></DialogHeader>
        {!data ? <Loading /> : data.html ? <EmailFrame html={data.html} testid="email-log-frame" /> : <p className="text-sm text-slate-500">No se generó contenido.</p>}
      </DialogContent>
    </Dialog>
  );
}

function Pick({ label, value, options, onChange, testid }) {
  return (
    <div><label className="field-label" htmlFor={testid}>{label} <span className="text-rose-500">*</span></label>
      <select id={testid} className="field-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        <option value="">Seleccione…</option>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
  );
}

function Schedule({ value, onChange, testid }) {
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={value.on} onChange={(e) => onChange({ ...value, on: e.target.checked })} data-testid={`${testid}-toggle`} />Programar para una fecha y hora (America/Chicago)</label>
      {value.on && <div className="mt-3 grid grid-cols-2 gap-3">
        <DateInput value={value.date} onChange={(d) => onChange({ ...value, date: d })} testid={`${testid}-date`} />
        <input type="time" className="field-input" value={value.time} onChange={(e) => onChange({ ...value, time: e.target.value })} data-testid={`${testid}-time`} aria-label="Hora" />
      </div>}
    </div>
  );
}
const schedValue = (s) => (s.on ? (s.date && s.time ? `${s.date}T${s.time}` : "invalid") : null);

function SendDialog({ open, onClose, title, description, testid, children, onSend }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (open) { setErr(""); } }, [open]);
  const go = async () => { setBusy(true); setErr(""); try { await onSend(); onClose(); } catch (e) { setErr(e.message?.startsWith("!") ? e.message.slice(1) : errMsg(e)); } finally { setBusy(false); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle><DialogDescription>{description}</DialogDescription></DialogHeader>
        <div className="space-y-4">{children}
          {err && <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid={`${testid}-error`}>{err}</p>}</div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid={`${testid}-cancel`}>Cancelar</Button>
          <Button onClick={go} disabled={busy} data-testid={`${testid}-save`}>Confirmar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManualSend({ open, onOpenChange, templates, onSent }) {
  const clients = useClientOptions();
  const [sel, setSel] = useState({});
  const [sched, setSched] = useState({ on: false, date: "", time: "09:00" });
  const need = NEEDS[sel.template_key];
  const list = { invoice: "/invoices", renewal: "/renewals", project: "/projects" }[need];
  const { data: items } = useApi(open && need && sel.client_id ? list : null, { client_id: sel.client_id });
  const related = (items || []).filter((i) => need !== "invoice" || i.number).map((i) => ({ value: i.id,
    label: need === "invoice" ? `${i.number} · ${money(i.total)} · ${i.status}` : need === "renewal" ? `${i.service_type} · ${i.name} · vence ${fmtDate(i.expiry_date)}` : `${i.name} · ${i.status}` }));
  const relKey = REL_KEY[need];
  const close = () => { onOpenChange(false); setSel({}); setSched({ on: false, date: "", time: "09:00" }); };
  const tpl = (templates || []).filter((t) => t.category !== "Marketing");
  return (
    <SendDialog open={open} onClose={close} title="Enviar correo manual" testid="manual-send-form" description="Se envía aunque la plantilla esté desactivada y aunque el cliente haya desactivado ese tipo de aviso."
      onSend={async () => {
        if (!sel.template_key || !sel.client_id || (need && !sel[relKey])) throw new Error("!Complete todos los campos obligatorios.");
        const at = schedValue(sched);
        if (at === "invalid") throw new Error("!Elija la fecha y la hora del envío.");
        const r = await api.post("/notifications/send", { ...sel, scheduled_at: at });
        toast.success(`Correo: ${r.data.status}`); onSent();
      }}>
      <Pick label="Plantilla" testid="manual-send-form-template_key" value={sel.template_key} options={tpl.map((t) => ({ value: t.key, label: t.name }))} onChange={(v) => setSel({ template_key: v, client_id: sel.client_id })} />
      <Pick label="Cliente" testid="manual-send-form-client_id" value={sel.client_id} options={clients} onChange={(v) => setSel({ template_key: sel.template_key, client_id: v })} />
      {need && <Pick label={REL_LABEL[need]} testid={`manual-send-form-${relKey}`} value={sel[relKey]} options={related} onChange={(v) => setSel({ ...sel, [relKey]: v })} />}
      {need && sel.client_id && !related.length && <p className="text-xs text-slate-500">Este cliente no tiene registros de este tipo.</p>}
      <Schedule value={sched} onChange={setSched} testid="manual-send-schedule" />
    </SendDialog>
  );
}

export function CampaignDialog({ open, onOpenChange, templates, onSent, consented }) {
  const [key, setKey] = useState("");
  const [sched, setSched] = useState({ on: true, date: "", time: "09:00" });
  const mk = (templates || []).filter((t) => t.category === "Marketing");
  const pending = /\[PENDIENTE/i.test(`${mk.find((t) => t.key === key)?.subject || ""} ${mk.find((t) => t.key === key)?.body || ""}`);
  return (
    <SendDialog open={open} onClose={() => onOpenChange(false)} title="Campaña de marketing" testid="campaign-form"
      description={`Solo se envía a los ${consented ?? 0} cliente(s) con consentimiento. Cada correo incluye el enlace de baja en un clic.`}
      onSend={async () => {
        if (!key) throw new Error("!Elija la plantilla de marketing.");
        const at = schedValue(sched);
        if (at === "invalid") throw new Error("!Elija la fecha y la hora del envío.");
        const r = await api.post("/notifications/campaign", { template_key: key, scheduled_at: at });
        toast.success(`${r.data.recipients} destinatario(s) · ${r.data.excluded_without_consent} excluido(s) sin consentimiento`); onSent();
      }}>
      <Pick label="Plantilla de marketing" testid="campaign-form-template" value={key} options={mk.map((t) => ({ value: t.key, label: t.name }))} onChange={setKey} />
      {pending && <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="campaign-pending-warning">La plantilla todavía tiene textos [PENDIENTE]. Edítela en Plantillas antes de enviarla.</p>}
      <Schedule value={sched} onChange={setSched} testid="campaign-schedule" />
    </SendDialog>
  );
}

function Actions({ r, act }) {
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  if (["Fallido", "Rebotado"].includes(r.status) && r.to !== "—") return <Button size="sm" variant="outline" onClick={stop(() => act("retry", r))} data-testid={`email-retry-${r.id}`}><RotateCw className="mr-1 h-3.5 w-3.5" />Reintentar</Button>;
  if (r.status === "Programado") return <Button size="sm" variant="outline" onClick={stop(() => act("cancel", r))} data-testid={`email-cancel-${r.id}`}><Ban className="mr-1 h-3.5 w-3.5" />Cancelar</Button>;
  if (r.status === "Enviado") return <Button size="sm" variant="ghost" onClick={stop(() => act("bounce", r))} data-testid={`email-bounce-${r.id}`}><MailX className="mr-1 h-3.5 w-3.5" />No llegó</Button>;
  return null;
}

export function EmailHistory({ templates, consented }) {
  const [f, setF] = useState({});
  const { data, loading, error, reload } = useApi("/email-log", f);
  const [view, setView] = useState(null);
  const [sending, setSending] = useState(false);
  const [campaign, setCampaign] = useState(false);
  const act = async (kind, r) => {
    try {
      if (kind === "bounce") {
        const reason = window.prompt("¿Qué pasó? (por ejemplo: el cliente no lo recibió)");
        if (!reason || reason.trim().length < 3) return;
        await api.post(`/email-log/${r.id}/bounce`, { reason }); toast.success("Marcado como no entregado");
      } else { const x = await api.post(`/email-log/${r.id}/${kind}`); toast.success(kind === "retry" ? `Reintento: ${x.data.status}` : "Envío programado cancelado"); }
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <FilterBar values={f} onChange={setF} testid="email-log-filters" filters={[
        { key: "status", label: "Estado", options: optionList(EMAIL_STATUSES) },
        { key: "template_key", label: "Plantilla", options: (templates || []).map((t) => ({ value: t.key, label: t.name })) }]}>
        <div className="flex flex-wrap gap-2 sm:ml-auto">
          <Button variant="outline" onClick={() => setCampaign(true)} data-testid="campaign-button"><Megaphone className="mr-2 h-4 w-4" />Campaña de marketing</Button>
          <Button onClick={() => setSending(true)} data-testid="manual-send-button"><Send className="mr-2 h-4 w-4" />Enviar correo manual</Button>
        </div>
      </FilterBar>
      <ErrorBox text={error} />
      <DataTable testid="email-log-table" loading={loading} rows={data} empty="Todavía no hay envíos." onRowClick={(r) => setView(r.id)}
        columns={[{ key: "created_at", label: "Fecha", render: (r) => <span className="text-xs">{fmtDateTime(r.scheduled_at || r.created_at)}{r.scheduled_at && <span className="block text-slate-500">programado</span>}</span> },
          { key: "template_name", label: "Plantilla", render: (r) => <span>{r.template_name}{r.test ? " (prueba)" : r.manual ? " (manual)" : ""}</span> },
          { key: "to", label: "Destinatario" }, { key: "client_name", label: "Cliente" },
          { key: "status", label: "Estado", render: (r) => <span><StatusBadge status={r.status} />{r.error && <span className="mt-1 block max-w-[220px] truncate text-xs text-rose-600" title={r.error}>{r.error}</span>}</span> },
          { key: "attempts", label: "Intentos" },
          { key: "act", label: "", render: (r) => <Actions r={r} act={act} /> }]} />
      <LogViewer id={view} onClose={() => setView(null)} />
      <ManualSend open={sending} onOpenChange={setSending} templates={templates} onSent={reload} />
      <CampaignDialog open={campaign} onOpenChange={setCampaign} templates={templates} onSent={reload} consented={consented} />
    </div>
  );
}
